import * as React from "react";
import clsx from "clsx";
import "./Table.scss";
import { Download, Info, TriangleDown, TriangleUp } from "@/icons";
import { ActionIconText } from "@/components";
import { CommonIconsType } from "@/types/icons";
import { InfoPopupProps } from "../InfoPopup";
import { useEffect } from "react";

// per le celle che vanno renderizzate su due righe
export interface DoubleLineData {
  firstLine: string;
  secondLine: string;
}

//per le celle che renderizzano un link o un bottone
export interface ActionData {
  label: string | React.ReactNode;
  ariaLabel: string;
  type: "link" | "button";
  link?: string;
  action?: () => void;
}

export interface ColumnConfig {
  label: string;
  ariaLabel: string;
  sortable: boolean;
  action?: () => void;
  iconAction?: () => void;
  popupInfo?: InfoPopupProps;
  icon?: React.ReactNode | CommonIconsType;
  iconPosition?: "left" | "right";
  key?: string;
  type?: "string" | "date" | "number";
  scope?: "col" | "row" | "colgroup" | "rowgroup";
}

export interface TableRowData {
  [key: string]: string | DoubleLineData | ActionData;
}

export interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  className?: string;
  children?: React.ReactNode;
}

export interface TableBodyProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  className?: string;
  children?: React.ReactNode;
}

export interface TableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  className?: string;
  children?: React.ReactNode;
}

export interface TableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  className?: string;
  children?: React.ReactNode;
}

export interface TableCellBaseProps
  extends Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "content"> {
  type?: "double-line" | "action";
}

export interface TableCellProps extends TableCellBaseProps {
  content: string | DoubleLineData | ActionData;
}

export interface PaginationProps {
  currentPage: number;
  totalResults: number;
  currentResultsPerPage?: number; // used to display 8 di 188 if user is in the last page
  resultsPerPage: number;
  setCurrentPage: (page: number) => void;
  onPageChange?: (page: number) => void;
  className?: string;
  serverSide?: boolean;
  totalItems?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
}

export interface TableProps
  extends React.TableHTMLAttributes<HTMLTableElement> {
  className?: string;
  columns: ColumnConfig[];
  data: TableRowData[];
  pagination?: boolean;
  initialCurrentPage?: number;
  resultsPerPage?: number;
  defaultSortDirection?: "asc" | "desc";
  setSelectedSort?: (sortConfig: {
    key: string;
    direction: "asc" | "desc";
  }) => void;
  initialSortConfig?: { key: string; direction: "asc" | "desc" };
  serverSidePagination?: boolean;
  totalItems?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageChange?: (page: number) => void;
  variant?: "default" | "exemptions";
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      columns,
      data,
      pagination,
      initialCurrentPage = 1,
      resultsPerPage = 10,
      defaultSortDirection = "desc",
      setSelectedSort,
      initialSortConfig,
      serverSidePagination = false,
      totalItems,
      hasNextPage,
      hasPreviousPage,
      onNextPage,
      onPreviousPage,
      onPageChange,
      ...props
    },
    ref,
  ) => {
    const [sortConfig, setSortConfig] = React.useState<{
      key: string;
      direction: "asc" | "desc";
    }>(
      initialSortConfig || {
        key: columns[0].key || columns[0].label,
        direction: defaultSortDirection,
      },
    );
    const [currentPage, setCurrentPage] = React.useState(initialCurrentPage);

    useEffect(() => {
      if (initialSortConfig) {
        setSortConfig(initialSortConfig);
      }
    }, [initialSortConfig]);

    const sortedData = React.useMemo(() => {
      return [...data].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        const column = columns.find(
          (col) => (col.key || col.label) === sortConfig.key,
        );

        if (typeof aValue === "string" && typeof bValue === "string") {
          if (column?.type === "date") {
            // Convert Italian date format (DD/MM/YYYY - HH:mm) to Date object
            const parseDate = (dateStr: string) => {
              const [datePart, timePart] = dateStr.split(" - ");
              const [day, month, year] = datePart.split("/").map(Number);
              const [hours, minutes] = timePart.split(":").map(Number);
              return new Date(year, month - 1, day, hours, minutes);
            };

            const dateA = parseDate(aValue);
            const dateB = parseDate(bValue);
            return sortConfig.direction === "asc"
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          }

          if (column?.type === "number") {
            const numA = parseFloat(aValue);
            const numB = parseFloat(bValue);
            return sortConfig.direction === "asc" ? numA - numB : numB - numA;
          }

          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (isDoubleLineData(aValue) && isDoubleLineData(bValue)) {
          return sortConfig.direction === "asc"
            ? aValue.firstLine.localeCompare(bValue.firstLine)
            : bValue.firstLine.localeCompare(aValue.firstLine);
        }

        return 0;
      });
    }, [data, sortConfig, columns]);

    const handleSort = (columnKey: string) => {
      const newSortConfig = {
        key: columnKey,
        direction:
          sortConfig.key === columnKey && sortConfig.direction === "asc"
            ? "desc"
            : "asc",
      } as { key: string; direction: "asc" | "desc" };

      setSortConfig(newSortConfig);
      if (setSelectedSort) {
        setSelectedSort(newSortConfig);
      }
    };

    const startIndex =
      pagination && !serverSidePagination
        ? (currentPage - 1) * resultsPerPage
        : 0;
    const endIndex =
      pagination && !serverSidePagination
        ? Math.min(startIndex + resultsPerPage, sortedData.length)
        : sortedData.length;
    const currentPageData = serverSidePagination
      ? sortedData
      : sortedData.slice(startIndex, endIndex);

    return (
      <div className="position-relative w-full">
        <table
          ref={ref}
          className={clsx("table", className)}
          role="table"
          {...props}
        >
          <TableHeader className="table__header">
            <TableRow>
              {columns.map((column, index) => (
                <TableHead
                  key={`${column.label}-${index}`}
                  config={column}
                  sortConfig={sortConfig}
                  onSort={() =>
                    column.sortable && handleSort(column.key || column.label)
                  }
                  variant={props.variant}
                />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="table__body">
            {currentPageData.map((row, rowIndex) => (
              <TableRow key={rowIndex} className="table__row">
                {columns.map((column, cellIndex) => {
                  const key = column.key || column.label;
                  const value = row[key];
                  return (
                    <TableCell
                      key={`${key}-${cellIndex}`}
                      content={value || ""}
                      type={
                        value && isDoubleLineData(value)
                          ? "double-line"
                          : value && isActionData(value)
                            ? "action"
                            : undefined
                      }
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </table>
        {pagination && (
          <Pagination
            currentPage={currentPage}
            totalResults={
              serverSidePagination ? totalItems || 0 : sortedData.length
            }
            currentResultsPerPage={data.length}
            resultsPerPage={resultsPerPage}
            setCurrentPage={setCurrentPage}
            onPageChange={onPageChange}
            serverSide={serverSidePagination}
            totalItems={totalItems}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
          />
        )}
      </div>
    );
  },
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <thead
      ref={ref}
      className={clsx("[&_tr]:border-b-0", className)}
      {...props}
    >
      {children}
    </thead>
  ),
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <tbody
      ref={ref}
      className={clsx("[&_tr:last-child]:border-0", className)}
      {...props}
    >
      {children}
    </tbody>
  ),
);
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, ...props }, ref) => (
    <tr
      ref={ref}
      className={clsx(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  TableHeadProps & {
    config: ColumnConfig;
    sortConfig: { key: string; direction: "asc" | "desc" };
    onSort?: () => void;
    variant?: "default" | "exemptions";
  }
>(({ className, config, sortConfig, onSort, ...props }, ref) => {
  const isSorted = sortConfig.key === (config.key || config.label);
  const sortDirection = isSorted ? sortConfig.direction : null;

  const getAriaSortValue = (): "ascending" | "descending" => {
    return sortDirection === "asc" ? "ascending" : "descending";
  };

  const renderIcon = (icon: React.ReactNode | CommonIconsType) => {
    switch (icon) {
      case "info":
        return <Info />;
      case "download":
        return <Download />;
      default:
        return icon;
    }
  };

  return (
    <th
      ref={ref}
      className={clsx(
        "table__cell table__cell--header",
        {
          "table__cell--sortable": config.sortable,
          "table__cell--action": config.action,
        },
        className,
      )}
      onClick={config.sortable ? onSort : config.action}
      aria-label={config.ariaLabel}
      aria-sort={isSorted ? getAriaSortValue() : undefined}
      scope={config.scope || "col"}
      {...props}
    >
      <div className="table__cell--header--content">
        <div>
          <ActionIconText
            icon={renderIcon(config.icon)}
            noIcon={!renderIcon(config.icon)}
            textLabel={config.label}
            iconPosition={config.iconPosition}
            handleIconAction={() => {
              if (config.iconAction) {
                config.iconAction();
              }
            }}
            popupInfo={config.popupInfo}
            positionPopUp={config.popupInfo?.positionPopUp}
            classNameText={
              props.variant && "fw-bold text-slate-28 fs-custom md-fs-6"
            }
          />
        </div>
        {config.sortable && (
          <span className="sort-indicator">
            <TriangleUp
              filled={isSorted && sortDirection === "desc"}
              className={clsx("sort-indicator__triangle", {
                active: isSorted && sortDirection === "desc",
              })}
            />
            <TriangleDown
              filled={isSorted && sortDirection === "asc"}
              className={clsx("sort-indicator__triangle", {
                active: isSorted && sortDirection === "asc",
              })}
            />
          </span>
        )}
      </div>
    </th>
  );
});
TableHead.displayName = "TableHead";

const DoubleLineContent: React.FC<{ data: DoubleLineData }> = ({ data }) => (
  <>
    <div className="table__cell--firstLine">{data.firstLine}</div>
    <div className="table__cell--secondLine">{data.secondLine}</div>
  </>
);

const ActionContent: React.FC<{ data: ActionData }> = ({ data }) => {
  if (data.type === "link" && data.link) {
    return (
      <a href={data.link} aria-label={data.ariaLabel}>
        {data.label}
      </a>
    );
  }
  return (
    <button onClick={data.action} aria-label={data.ariaLabel}>
      {data.label}
    </button>
  );
};

// check if we are rendering a double line data
const isDoubleLineData = (
  content: TableCellProps["content"],
): content is DoubleLineData => {
  return (
    typeof content === "object" &&
    content !== null &&
    "firstLine" in content &&
    "secondLine" in content
  );
};

// check if we are rendering an action data
const isActionData = (
  content: TableCellProps["content"],
): content is ActionData => {
  return (
    typeof content === "object" &&
    content !== null &&
    "label" in content &&
    "type" in content
  );
};

const TableCellContent: React.FC<{ content: TableCellProps["content"] }> = ({
  content,
}) => {
  if (typeof content === "string") {
    return <>{content}</>;
  }
  if (isDoubleLineData(content)) {
    return <DoubleLineContent data={content} />;
  }
  if (isActionData(content)) {
    return <ActionContent data={content} />;
  }
  return null;
};

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, content, type, ...props }, ref) => (
    <td
      ref={ref}
      className={clsx(
        "table__cell",
        {
          "table__cell--double-line": type === "double-line",
          "table__cell--action": type === "action",
        },
        className,
      )}
      {...props}
    >
      <TableCellContent content={content} />
    </td>
  ),
);
TableCell.displayName = "TableCell";

const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  totalResults,
  resultsPerPage,
  currentResultsPerPage,
  setCurrentPage,
  onPageChange,
  className,
  serverSide = false,
  totalItems,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
}) => {
  const totalPages = serverSide
    ? Math.ceil((totalItems || totalResults) / resultsPerPage)
    : Math.ceil(totalResults / resultsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    if (serverSide && onNextPage) {
      onNextPage();
    }
    handlePageChange(nextPage);
  };

  const handlePreviousPage = () => {
    const prevPage = currentPage - 1;
    if (serverSide && onPreviousPage) {
      onPreviousPage();
    }
    handlePageChange(prevPage);
  };

  const isNextDisabled = serverSide ? !hasNextPage : currentPage === totalPages;
  const isPrevDisabled = serverSide ? !hasPreviousPage : currentPage === 1;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className={clsx("pagination", className)}>
      <div className="pagination__info">
        Stai visualizzando{" "}
        <span className="pagination__info__resultsPerPage">
          {serverSide
            ? currentResultsPerPage
            : currentPage === totalPages && totalResults % resultsPerPage !== 0
              ? totalResults % resultsPerPage
              : resultsPerPage}
        </span>{" "}
        di {serverSide ? totalItems || totalResults : totalResults} risultati
      </div>
      <div className="pagination__nav">
        <button
          className={clsx(
            "pagination__button",
            isPrevDisabled && "pagination__button--disabled",
          )}
          onClick={handlePreviousPage}
          disabled={isPrevDisabled}
          aria-label="Previous page"
        >
          <svg
            className="pagination__button-arrow"
            viewBox="0 0 24 24"
            aria-hidden="true"
            role="presentation"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {getPageNumbers().map((page, index) =>
          page === "..." ? (
            <span key={`ellipsis-${index}`} className="pagination__ellipsis">
              ...
            </span>
          ) : (
            <button
              key={page}
              className={clsx(
                "pagination__button",
                currentPage === page && "pagination__button--active",
              )}
              onClick={() => handlePageChange(page as number)}
              disabled={page === currentPage}
            >
              {page}
            </button>
          ),
        )}

        <button
          className={clsx(
            "pagination__button",
            isNextDisabled && "pagination__button--disabled",
          )}
          onClick={handleNextPage}
          disabled={isNextDisabled}
          aria-label="Next page"
        >
          <svg
            className="pagination__button-arrow"
            viewBox="0 0 24 24"
            aria-hidden="true"
            role="presentation"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Pagination,
};
